import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { useCart } from '../../store/cartContext';
import { DeliveryNotice } from '../../components/shared/DeliveryNotice';

export default function CartPage() {
  const { items, removeItem, updateQuantity, totalPrice, shipping, grandTotal, clearCart } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <ShoppingBag size={56} className="text-muted-foreground/30 mx-auto mb-6" />
        <h1 className="font-serif text-2xl font-bold text-foreground mb-3">Korpa je prazna</h1>
        <p className="text-muted-foreground font-body-serif mb-8">
          Niste još nista dodali u korpu. Pogledajte našu ponudu domaće proizvoda.
        </p>
        <Link to="/proizvodi">
          <Button className="bg-primary text-primary-foreground hover:bg-primary-light font-ui">
            Pogledaj Proizvode
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-bold text-foreground">Vaša Korpa</h1>
        <p className="text-muted-foreground font-ui text-sm mt-1">{items.length} {items.length === 1 ? 'stavka' : 'stavki'}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Items */}
        <div className="lg:col-span-2 space-y-4">
          <AnimatePresence>
            {items.map(item => (
              <motion.div
                key={item.product.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-card rounded-xl p-4 sm:p-5 shadow-card border border-border flex gap-4"
              >
                <Link to={`/proizvodi/${item.product.id}`} className="shrink-0">
                  <img
                    src={item.product.primaryImageUrl}
                    alt={item.product.name}
                    className="h-24 w-24 object-cover rounded-xl"
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <div>
                      <span className="text-xs text-muted-foreground font-ui">{item.product.categoryName}</span>
                      <h3 className="font-serif font-semibold text-foreground text-base leading-snug">
                        {item.product.name}
                      </h3>
                      <p className="text-muted-foreground text-xs font-ui mt-0.5">
                        {item.product.price.toLocaleString('sr-RS')} RSD / {item.product.unit}
                      </p>
                    </div>
                    <button
                      onClick={() => removeItem(item.product.id)}
                      className="p-2 text-muted-foreground hover:text-destructive transition-colors shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-border rounded-xl overflow-hidden">
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        className="px-3 py-2 hover:bg-muted transition-colors"
                      >
                        <Minus size={14} className="text-foreground" />
                      </button>
                      <span className="px-3 text-sm font-ui font-semibold text-foreground">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        className="px-3 py-2 hover:bg-muted transition-colors"
                      >
                        <Plus size={14} className="text-foreground" />
                      </button>
                    </div>
                    <span className="font-ui font-bold text-foreground">
                      {(item.product.price * item.quantity).toLocaleString('sr-RS')} RSD
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          <button
            onClick={clearCart}
            className="text-muted-foreground hover:text-destructive text-sm font-ui transition-colors flex items-center gap-1.5"
          >
            <Trash2 size={14} />
            Isprazni korpu
          </button>
        </div>

        {/* Summary */}
        <div>
          <div className="bg-card rounded-xl shadow-card border border-border p-6 sticky top-28">
            <h2 className="font-serif text-xl font-bold text-foreground mb-5">Pregled narudžbine</h2>
            <div className="space-y-3 mb-5">
              {items.map(item => (
                <div key={item.product.id} className="flex justify-between text-sm font-ui">
                  <span className="text-muted-foreground">{item.product.name} x{item.quantity}</span>
                  <span className="text-foreground">{(item.product.price * item.quantity).toLocaleString('sr-RS')} RSD</span>
                </div>
              ))}
            </div>
            <Separator className="my-4" />
            <div className="space-y-2.5 mb-5">
              <div className="flex justify-between text-sm font-ui">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground">{totalPrice.toLocaleString('sr-RS')} RSD</span>
              </div>
              <div className="flex justify-between text-sm font-ui">
                <span className="text-muted-foreground">Dostava</span>
                <span className={shipping === 0 ? 'text-primary font-medium' : 'text-foreground'}>
                  {shipping === 0 ? 'BESPLATNO' : `${shipping} RSD`}
                </span>
              </div>
            </div>
            <Separator className="mb-4" />
            <div className="flex justify-between font-semibold font-ui mb-6">
              <span className="text-foreground text-base">Ukupno</span>
              <span className="text-primary text-xl">{grandTotal.toLocaleString('sr-RS')} RSD</span>
            </div>
            {shipping > 0 && <DeliveryNotice variant="compact" className="mb-4" />}
            <Button
              size="lg"
              onClick={() => navigate('/checkout')}
              className="w-full bg-primary text-primary-foreground hover:bg-primary-light font-ui shadow-btn"
            >
              Nastavi na placanje
              <ArrowRight size={16} className="ml-2" />
            </Button>
            <Link to="/proizvodi">
              <Button variant="ghost" className="w-full mt-2 font-ui text-muted-foreground">
                Nastavi kupovinu
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
